import { ReactNode, useState } from 'react';

import { AnimatePresence, MotionConfig } from 'motion/react';

import {
  ExpandableCard,
  type RenderBottomPayload,
} from './components/ExpandableCard';
import { ExpandableCardDialog } from './components/ExpandableCardDialog';
import { CardGrid } from '@/components/layouts/CardGrid';

interface ExpandableCardGridItem {
  id: string;
  title: string;
  description?: string;
}

interface ExpandableCardGridProps<T extends ExpandableCardGridItem> {
  items: T[];
  // make optional for cases where no expanded content is needed, e.g. when using the component just for the hover effect and card bottom content
  renderExpanded?: (item: T) => ReactNode;
  renderCardBottom?: (payload: RenderBottomPayload) => ReactNode;
}

export const ExpandableCards = <T extends ExpandableCardGridItem>({
  items,
  renderExpanded,
  renderCardBottom,
}: ExpandableCardGridProps<T>) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedItem = items.find((item) => item.id === selectedId) ?? null;

  return (
    <MotionConfig reducedMotion="user">
      <CardGrid items={items} isLastPage={true} showMore={() => {}}>
        {(item) => (
          <ExpandableCard
            {...item}
            id={item.id}
            onSelect={setSelectedId}
            renderBottom={renderCardBottom}
          />
        )}
      </CardGrid>
      <AnimatePresence>
        {selectedItem && renderExpanded && (
          <ExpandableCardDialog
            key={selectedItem.id}
            cardId={selectedItem.id}
            onClose={() => setSelectedId(null)}
          >
            {renderExpanded(selectedItem)}
          </ExpandableCardDialog>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
};
